import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Mail, ArrowRight, ShieldCheck } from 'lucide-react';

interface EmailGateProps {
  onAccessGranted: () => void;
}

const STORAGE_KEY = 'ems_calculator_email_verified';
const EMAIL_STORAGE_KEY = 'ems_calculator_email';

export const EmailGate = ({ onAccessGranted }: EmailGateProps) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const verified = localStorage.getItem(STORAGE_KEY);
    if (verified === 'true') {
      onAccessGranted();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast({
        title: 'Hibás email cím',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('save-email-to-sheets', {
        body: { email },
      });

      if (error) throw error;

      if (data?.authorized) {
        localStorage.setItem(STORAGE_KEY, 'true');
        localStorage.setItem(EMAIL_STORAGE_KEY, email);
        toast({
          title: 'Sikeres hitelesítés!',
          description: 'Hozzáférés engedélyezve a kalkulátorhoz.',
        });
        onAccessGranted();
      } else {
        toast({
          title: 'Hozzáférés megtagadva',
          description: 'Ez az email cím nincs engedélyezve. Kérjen hozzáférést az info@btes.hu címen.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error verifying email:', error);
      toast({
        title: 'Hiba történt',
        description: 'Kérjük, próbálja újra később.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
      <div className="w-full max-w-md">
        <div className="bg-card border border-border rounded-2xl shadow-2xl p-8 space-y-6">
          <div className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <ShieldCheck className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">
              Hozzáférés ellenőrzése
            </h1>
            <p className="text-muted-foreground">
              Ez a kalkulátor csak regisztrált partnerek számára érhető el. Kérjük, adja meg az engedélyezett email címét.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground">
                Email cím
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="pelda@ceg.hu"
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              size="lg"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Ellenőrzés...' : 'Belépés'}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>

          <p className="text-xs text-center text-muted-foreground">
            Ha nincs hozzáférése és szeretne regisztrálni, kérjük írjon nekünk:{' '}
            <a href="mailto:info@btes.hu" className="text-primary hover:underline font-medium">
              info@btes.hu
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};